import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  useEffect(() => {
    console.log("✅ DATA PROVIDER MOUNTED");
    return () => console.log("❌ DATA PROVIDER UNMOUNTED");
  }, []);

  const { user: currentUser } = useSelector((state) => state.auth);
  console.log("🌐 DATA PROVIDER RENDER: User exists:", !!currentUser);

  // Fetch all assets
  const { data: allAssets = [], isLoading: assetsLoading, error: assetsError } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assets')
        .select(`
            *, 
            departments ( name ), 
            suppliers ( company_name ),
            locations ( name ),
            maintenance ( cost ),
            registered_by_profile:registered_by(full_name),
            loans (
                status,
                employees ( id, full_name, email, department_id, departments ( name ) ),
                departments ( id, name ),
                assigned_by_profile:assigned_by(full_name)
            )
        `);
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser,
  });

  // Fetch all employees (personnel)
  const { data: allPersonnel = [], isLoading: personnelLoading, error: personnelError } = useQuery({
    queryKey: ['allPersonnel'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('*, departments(name)');
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser,
  });

  // Role-Based Filtering Logic
  const filteredAssets = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'admin' || currentUser.role === 'system_admin' || currentUser.role === 'it_staff') {
      return allAssets; // Admin/IT Staff sees everything
    }

    // PIC only sees their department's assets
    // Using current_department_id, department name, or checking active loans to their personnel
    return allAssets.filter(asset => 
      asset.current_department_id === currentUser.department_id || 
      asset.departments?.name === currentUser.department_name ||
      asset.loans?.some(loan => 
        (loan.status === 'active' || loan.status === 'In Use' || loan.status === 'overdue') && 
        loan.employees?.department_id === currentUser.department_id
      )
    );
  }, [allAssets, currentUser]);

  const filteredPersonnel = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === 'admin' || currentUser.role === 'system_admin' || currentUser.role === 'it_staff') {
      return allPersonnel; // Admin/IT Staff sees everything
    }

    // PIC only sees their department's personnel
    return allPersonnel.filter(person => 
      person.department_id === currentUser.department_id ||
      person.departments?.name === currentUser.department_name
    );
  }, [allPersonnel, currentUser]);

  const value = {
    assets: filteredAssets,
    personnel: filteredPersonnel,
    allAssets,
    allPersonnel,
    isLoading: assetsLoading || personnelLoading,
    error: assetsError || personnelError,
    currentUser
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
